import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Container } from 'react-bootstrap';
import { FaBox, FaExchangeAlt, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import axios from 'axios';
import GLOBALS from '../../utils/Globals';
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalCases: 0,
    publishedProducts: 0,
    validProducts: 0,
    invalidProducts: 0,
    validCases: 0,
    invalidCases: 0,
    loading: true,
    s3ServiceAvailable: true
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch product data from PDF docs service
        const baseURLGetAllPDFDocs =
          process.env.REACT_APP_ENV === "PROD"
            ? GLOBALS.PROD.baseURLGetAllPDFDocs
            : GLOBALS.DEV.baseURLGetAllPDFDocs;

        // Fetch published files count from S3 service (same as ViewExportedFiles)
        const baseURLGetS3 =
          process.env.REACT_APP_ENV === "PROD"
            ? GLOBALS.PROD.baseURLGetS3
            : GLOBALS.DEV.baseURLGetS3;

        // Fetch both data sources with individual error handling
        const [productResponse, s3Response] = await Promise.allSettled([
          axios.get(baseURLGetAllPDFDocs),
          axios.get(baseURLGetS3)
        ]);

        const data = productResponse.status === 'fulfilled' ? productResponse.value.data : [];
        const s3Data = s3Response.status === 'fulfilled' ? s3Response.value.data : [];

        // Log any errors for debugging
        if (productResponse.status === 'rejected') {
          console.warn('Failed to fetch product data:', productResponse.reason?.message);
        }
        if (s3Response.status === 'rejected') {
          console.warn('Failed to fetch S3 data (published files):', s3Response.reason?.message);
        }

        // Calculate stats from the product data
        let totalProducts = 0;
        let totalCases = 0;
        let validProducts = 0;
        let invalidProducts = 0;
        let validCases = 0;
        let invalidCases = 0;

        // Debug: Log the data structure
        console.log('MongoDB data structure:', data);
        console.log('Data type:', typeof data);
        console.log('Is array:', Array.isArray(data));

        // Check if data is an array
        if (Array.isArray(data)) {
          console.log('Processing array data with', data.length, 'items');
          totalCases = data.length; // Each document represents a case

          data.forEach((item, index) => {
            // Debug: Log first few items
            if (index < 3) {
              console.log(`Item ${index}:`, item);
              console.log(`Item ${index} has products:`, !!item.products);
              console.log(`Item ${index} products type:`, typeof item.products);
              console.log(`Item ${index} products length:`, item.products?.length);
            }

            // Check case-level validation
            let caseHasValidGTIN = false;
            let caseHasInvalidGTIN = false;

            // Check case UPC validation
            if (item.case?.case_information?.['Case UPC']) {
              const caseUPC = item.case.case_information['Case UPC'];
              // Check if case has validation data
              if (item.validation?.case_upc_validation) {
                if (item.validation.case_upc_validation === true || item.validation.case_upc_validation === 'valid') {
                  caseHasValidGTIN = true;
                } else {
                  caseHasInvalidGTIN = true;
                }
              }
            }

            // Check if item has products array (this is the correct structure)
            if (item.products && Array.isArray(item.products)) {
              totalProducts += item.products.length; // Count all products in the array

              // Process each product for validation stats
              item.products.forEach((product, productIndex) => {
                // Debug: Log first few products
                if (index < 3 && productIndex < 3) {
                  console.log(`Document ${index}, Product ${productIndex}:`, product);
                  console.log(`Product validation:`, product.validation);
                  if (product.validation) {
                    console.log(`Product gtin_valid:`, product.validation.gtin_valid);
                    console.log(`Product gtin_validation:`, product.validation.gtin_validation);
                    if (product.validation.gtin_validation) {
                      console.log(`Product status:`, product.validation.gtin_validation.status);
                    }
                  }
                }

                // Count valid/invalid products - check the gtin_valid field
                let productIsValid = false;
                let productIsInvalid = false;

                // Check the gtin_valid field in validation section
                if (product.validation?.gtin_valid === true) {
                  productIsValid = true;
                } else if (product.validation?.gtin_valid === false) {
                  productIsInvalid = true;
                }

                // Fallback: Check legacy validation structures if gtin_valid is not present
                if (product.validation?.gtin_valid === undefined) {
                  // Check retail_pkg_upc_validation as fallback
                  if (product.validation?.retail_pkg_upc_validation === true) {
                    productIsValid = true;
                  } else if (product.validation?.retail_pkg_upc_validation === false) {
                    productIsInvalid = true;
                  }

                  // Check legacy gtin_validation.status as final fallback
                  if (!productIsValid && !productIsInvalid && product.validation?.gtin_validation?.status) {
                    const status = product.validation.gtin_validation.status.toLowerCase();
                    if (status === 'valid') {
                      productIsValid = true;
                    } else if (status === 'invalid') {
                      productIsInvalid = true;
                    }
                  }
                }

                // Count the product
                if (productIsValid) {
                  validProducts++;
                  caseHasValidGTIN = true;
                  console.log(`✅ Valid product found! Document ${index}, Product ${productIndex}, Count now: ${validProducts}`);
                } else if (productIsInvalid) {
                  invalidProducts++;
                  caseHasInvalidGTIN = true;
                  console.log(`❌ Invalid product found! Document ${index}, Product ${productIndex}, Count now: ${invalidProducts}`);
                } else {
                  console.log(`⚠️ Product missing validation data:`, {
                    documentIndex: index,
                    productIndex: productIndex,
                    hasValidation: !!product.validation,
                    hasGtinValid: product.validation?.gtin_valid !== undefined,
                    gtinValidValue: product.validation?.gtin_valid,
                    hasGtinValidation: !!(product.validation?.gtin_validation),
                    hasStatus: !!(product.validation?.gtin_validation?.status),
                    hasRetailPkgValidation: product.validation?.retail_pkg_upc_validation !== undefined
                  });
                }
              });
            } else if (item.metadata?.number_of_products) {
              // Alternative: check if there's a metadata field with product count
              totalProducts += item.metadata.number_of_products;
              console.log(`Using metadata product count: ${item.metadata.number_of_products}`);
            } else {
              console.log(`Document ${index} has no products array or metadata count`);
            }

            // Count case validation
            if (caseHasValidGTIN) {
              validCases++;
            } else if (caseHasInvalidGTIN) {
              invalidCases++;
            }
          });
        } else if (typeof data === 'object') {
          console.log('Processing object data');
          console.log('Object has product:', !!data.product);
          console.log('Product type:', typeof data.product);

          // If data is not an array but an object, try to find product
          if (data.product && typeof data.product === 'object') {
            totalProducts = 1; // Single product object

            const product = data.product;

            // Debug: Log the product
            console.log(`Direct product:`, product);
            console.log(`Direct product validation:`, product.validation);
            if (product.validation) {
              console.log(`Direct product gtin_validation:`, product.validation.gtin_validation);
              if (product.validation.gtin_validation) {
                console.log(`Direct product status:`, product.validation.gtin_validation.status);
              }
            }

            // Count valid/invalid products - be more explicit about the path
            if (product.validation &&
                product.validation.gtin_validation &&
                product.validation.gtin_validation.status) {

              const status = product.validation.gtin_validation.status;
              console.log(`Found validation status: "${status}"`);

              if (status === 'valid') {
                validProducts++;
                console.log(`Valid product found! Count now: ${validProducts}`);
              } else if (status === 'invalid') {
                invalidProducts++;
                console.log(`Invalid product found! Count now: ${invalidProducts}`);
              } else {
                console.log(`Unknown status: "${status}"`);
              }
            } else {
              console.log(`Product missing validation data:`, {
                hasValidation: !!product.validation,
                hasGtinValidation: !!(product.validation?.gtin_validation),
                hasStatus: !!(product.validation?.gtin_validation?.status)
              });
            }
          }
        }

        // Count published products from S3 files (same logic as ViewExportedFiles)
        let publishedProducts = 0;

        if (Array.isArray(s3Data)) {
          publishedProducts = s3Data.length; // Each file in S3 represents a published product
        } else if (s3Response.status === 'rejected') {
          // If S3 service is unavailable, show a message in console and use 0
          console.info('S3 service unavailable - published products count will show as 0');
          publishedProducts = 0;
        }

        console.log('=== FINAL DASHBOARD STATS ===');
        console.log('Total Cases:', totalCases);
        console.log('Total Products:', totalProducts);
        console.log('Published Products (from S3):', publishedProducts);
        console.log('Valid Cases (from MongoDB):', validCases);
        console.log('Invalid Cases (from MongoDB):', invalidCases);
        console.log('Valid Products (from MongoDB):', validProducts);
        console.log('Invalid Products (from MongoDB):', invalidProducts);
        console.log('S3 Files Count:', s3Data?.length || 0);
        console.log('MongoDB Response Status:', productResponse.status);
        console.log('S3 Response Status:', s3Response.status);
        console.log('================================');

        setStats({
          totalProducts,
          totalCases,
          publishedProducts,
          validProducts, // Use MongoDB data for valid products count
          invalidProducts, // Use MongoDB data for invalid products count
          validCases, // Use MongoDB data for valid cases count
          invalidCases, // Use MongoDB data for invalid cases count
          loading: false,
          s3ServiceAvailable: s3Response.status === 'fulfilled'
        });
      } catch (error) {
        console.error("Error fetching dashboard stats:", error);
        // If there's an error, still try to show what data we can
        setStats(prev => ({
          ...prev,
          loading: false,
          // If S3 service fails, only reset published products (valid/invalid come from MongoDB)
          publishedProducts: 0,
          s3ServiceAvailable: false
        }));
      }
    };

    fetchStats();
  }, []);

  const statCards = [
    {
      title: 'Total Cases',
      value: stats.totalCases,
      icon: <FaBox />,
      color: 'primary',
      description: `Total cases (${stats.totalProducts} products)`
    },
    {
      title: 'Published Products',
      value: stats.publishedProducts,
      icon: <FaExchangeAlt />,
      color: 'info',
      description: stats.s3ServiceAvailable
        ? 'Exported files in S3 storage'
        : 'S3 service unavailable'
    },
    {
      title: 'Valid Products',
      value: stats.validProducts,
      icon: <FaCheckCircle />,
      color: 'success',
      description: `Products with valid GTIN (${stats.validCases} cases)`
    },
    {
      title: 'Invalid Products',
      value: stats.invalidProducts,
      icon: <FaExclamationTriangle />,
      color: 'warning',
      description: `Products with invalid GTIN (${stats.invalidCases} cases)`
    }
  ];

  if (stats.loading) {
    return (
      <div className="dashboard-container">
        <Container>
          <div className="dashboard-loading">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Loading dashboard data...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Container>
        {/* <div className="dashboard-header">
          <h2>Product Dashboard</h2>
          <p className="text-muted">Overview of your product data</p>
        </div> */}
        
        <Row className="g-4">
          {statCards.map((card, index) => (
            <Col key={index} xs={12} sm={6} md={3}>
              <Card className={`dashboard-card dashboard-card-${card.color}`}>
                <Card.Body>
                  <div className="dashboard-card-icon">
                    {card.icon}
                  </div>
                  <div className="dashboard-card-content">
                    <h3 className="dashboard-card-value">{card.value.toLocaleString()}</h3>
                    <h6 className="dashboard-card-title">{card.title}</h6>
                    <p className="dashboard-card-description">{card.description}</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
    </div>
  );
};

export default Dashboard;
